/**
 * Tipos da ilha Laudo Exame (/laudos-medicos/:tab).
 */

/** Abas da tela. O slug é o próprio segmento do path. */
export type ReportsTab =
  | 'laudos-pendentes'
  | 'exames-laudados'
  | 'configuracoes'

export const REPORTS_TABS: ReportsTab[] = [
  'laudos-pendentes',
  'exames-laudados',
  'configuracoes'
]

/** Situação do laudo pendente, no formato que a API lê (`pending_status`). */
export type PendingStatus = 'expired' | 'expiring' | 'pending'

/**
 * Filtros da lista. A URL é a fonte de verdade: isto é só a forma tipada da
 * query (ver `lib/urlFilters.ts`).
 */
export interface ListFilters {
  patient: string
  pendingStatus: PendingStatus | null
  /** Ids de profissionais (como vieram da URL ou da busca). */
  professionals: string[]
  /** Ids de itens (exames). */
  items: string[]
  /** YYYY-MM-DD */
  start: string | null
  /** YYYY-MM-DD */
  end: string | null
  dateLimit: number | null
  page: number
}

export const EMPTY_FILTERS: ListFilters = {
  patient: '',
  pendingStatus: null,
  professionals: [],
  items: [],
  start: null,
  end: null,
  dateLimit: null,
  page: 1
}

/** Query de rota como o Vue Router entrega. */
export type RouteQueryValue = string | null | undefined | (string | null)[]
export type RouteQuery = Record<string, RouteQueryValue>

/** Query serializada (só chaves preenchidas). */
export type ListQuery = Record<string, string>

export interface Session {
  clinicId: string
  userId: string
  userName: string
}

export interface AppProps {
  /** `:tab` da rota. */
  tab?: string | null
  /** Query da rota (fonte de verdade dos filtros). */
  query?: RouteQuery
  /** Aplica a query de filtros na URL (o wrapper faz `router.replace`). */
  onFiltersChange: (query: ListQuery) => void
  /** Troca de aba (o wrapper faz `router.push` sem a query). */
  onTabChange: (tab: ReportsTab) => void
  /** Abre o detalhe do laudo (o wrapper limpa o Vuex e faz `router.push`). */
  onOpenReport: (path: string) => void
}

export interface Option {
  value: string
  label: string
}

// ── Dados da API ─────────────────────────────────────────────────────────────

export interface ApiPerson {
  id: string | number
  name: string
  picture?: string | null
}

export interface ApiPatient {
  id: string | number
  name: string
  picture?: string | null
  birthday?: string | null
  clinic?: { name?: string | null } | null
}

export interface ApiResponsible {
  id?: string | number
  person_id?: string | number
  person: ApiPerson
  active?: boolean
}

export interface ApiMedicalReportFile {
  id: string | number
  file_id: string | number | null
  file?: {
    type_id?: string | null
    patient?: ApiPatient | null
    created_at?: string | null
  } | null
  file_group?: unknown
  attendance_id: string | number | null
  attendance?: {
    status?: string | null
    updated_at?: string | null
    end_datetime?: string | null
    professional?: { name?: string | null } | null
  } | null
  item_id: string | number | null
  item?: {
    name?: string | null
    laudable_clinic_procedure?: { limit_time?: number | string | null } | null
  } | null
  procedure?: unknown
  labpacs_item_reports?: {
    patient?: ApiPatient | null
    created_at?: string | null
  } | null
  responsibles: ApiResponsible[]
  limit_datetime?: string | null
  created_at?: string | null
  updated_at?: string | null
  printed?: number | null
}

export interface MedicalReportsPage {
  rows: ApiMedicalReportFile[]
  total: number
  perPage: number
}

/** Corpo de `data` em GET getMedicalReportsFiles. */
export interface MedicalReportsRequest {
  patient?: string
  pending_status?: PendingStatus
  period: { start: string | null; end: string | null }
  professionals: string[]
  items: string[]
  date_limit?: number
  status: string[]
  clinic_id: string
}
